/**
 * BoltChat — Live conversational interface to BOLT AI
 *
 * Connects to /api/bolt/chat/stream (SSE) for real-time streaming responses.
 * Supports:
 *  - Streaming text responses
 *  - RAG source citations from uploaded documents
 *  - Intent detection with confirmation cards
 *  - Voice input via BoltVoiceInput
 *  - Conversation history (in-memory)
 */

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Sparkles,
  ArrowUpRight,
  FileText,
  CheckCircle,
  XCircle,
  Zap,
  X,
  ChevronDown,
} from 'lucide-react';
import BoltVoiceInput from './BoltVoiceInput';
import BoltChatComposer from './BoltChatComposer';
import BoltMarkdown from './BoltMarkdown';
import BoltReasoning, { describeBoltTool } from './BoltReasoning';
import BoltShiningText from './BoltShiningText';
import BoltDisambiguationCard from './BoltDisambiguationCard';
import BoltDataCard from './BoltDataCard';
import BoltPlanningCard from './BoltPlanningCard';
import { parseSSEBlock } from './sseParser';
import PlanningSessionStatusBadge from '../planning/PlanningSessionStatusBadge';
import { API_BASE_URL } from '../../../config/api.config';
import {
  getPlanningSession,
  mapSessionQuestionsToBoltCard,
  normalizePlanningSession,
  TERMINAL_PLANNING_STATUSES,
} from '../../../services/api/planningSessions';
import boltAPI from '../../../services/boltApi';
import { useBoltDocuments } from '../../../hooks/useBoltData';
import './BoltChat.css';

const WELCOME_MESSAGE =
  "I'm Bolt, your AI jobsite copilot. Ask about safety, procedures, crew status, or project data—what do you need?";

const PLANNING_LOADING_GATHERING =
  'Analyzing your jobsite context to draft a plan…';
const PLANNING_LOADING_REFINING =
  'Refining the plan based on your answers…';

function filterEligibleDocumentsForJobsite(jobsiteId, documents) {
  if (!jobsiteId || !documents?.length) return [];
  const jid = String(jobsiteId);
  return documents.filter((doc) => {
    if (doc.scope === 'company') return true;
    if (String(doc.siteId || '') === jid) return true;
    return false;
  });
}

function groupSourcesForSummary(sources) {
  if (!sources?.length) {
    return { totalSources: 0, documentCount: 0, groups: [] };
  }
  const byKey = new Map();
  for (const src of sources) {
    const key = String(src.documentId ?? src.title ?? 'unknown');
    const displayTitle =
      (src.title && String(src.title).trim()) ||
      (src.documentId != null && String(src.documentId)) ||
      'Unknown document';
    if (!byKey.has(key)) {
      byKey.set(key, { key, title: displayTitle, count: 0, sheets: [] });
    }
    const g = byKey.get(key);
    g.count += 1;
    if (g.title === 'Unknown document' && displayTitle !== 'Unknown document') {
      g.title = displayTitle;
    }
    if (src.sheetNumber != null && !g.sheets.includes(src.sheetNumber)) {
      g.sheets.push(src.sheetNumber);
    }
  }
  const groups = Array.from(byKey.values()).sort((a, b) => {
    if (b.count !== a.count) return b.count - a.count;
    return a.title.localeCompare(b.title, undefined, { sensitivity: 'base' });
  });
  return {
    totalSources: sources.length,
    documentCount: groups.length,
    groups,
  };
}

function prefersReducedMotion() {
  return typeof window !== 'undefined'
    && window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
}

function typewriterStep(remaining) {
  if (remaining <= 1) return remaining;
  if (remaining > 80) return Math.min(24, Math.ceil(remaining / 5));
  if (remaining > 28) return 6;
  if (remaining > 8) return 3;
  return 1;
}

function isEmptyStreamingAssistant(msg) {
  return (
    msg?.role === 'assistant'
    && msg.streaming
    && !msg.content
    && !msg.planningQuestions
    && !msg.disambiguation
    && !msg.contextData
    && !msg.intent
    && !(msg.sources?.length > 0)
    && msg.citedSources == null
    && !(msg.reasoning?.length > 0)
    && !msg.reasoningActive
  );
}

function sealAssistantReasoning(prev) {
  const updated = [...prev];
  const last = updated[updated.length - 1];
  if (last?.role !== 'assistant' || !last.reasoningActive) return prev;
  const started = last.reasoningStartedAt
    ? new Date(last.reasoningStartedAt).getTime()
    : Date.now();
  const hasParts = Array.isArray(last.reasoning) && last.reasoning.length > 0;
  updated[updated.length - 1] = {
    ...last,
    reasoningActive: false,
    reasoningMs: Date.now() - started,
    ...(hasParts ? {} : { reasoning: [] }),
  };
  return updated;
}

function appendAssistantReasoning(prev, part, { mergeThought = false } = {}) {
  const updated = [...prev];
  const last = updated[updated.length - 1];
  if (last?.role !== 'assistant') return prev;
  const nextParts = [...(last.reasoning || [])];
  const tail = nextParts[nextParts.length - 1];
  if (mergeThought && part.type === 'thought' && tail?.type === 'thought') {
    nextParts[nextParts.length - 1] = { ...tail, text: `${tail.text}${part.text}` };
  } else {
    nextParts.push(part);
  }
  updated[updated.length - 1] = {
    ...last,
    reasoning: nextParts,
    reasoningActive: true,
  };
  return updated;
}

export default function BoltChat({
  jobsiteId = null,
  onConversationChange,
  initialConversationId = null,
  planningSessionId,
  sessionStatus: sessionStatusProp,
  pendingQuestionRound = null,
  planningSessionHydrationKey = null,
  onPlanningSessionAnswersSubmit,
}) {
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: WELCOME_MESSAGE,
      timestamp: new Date(),
    },
  ]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [resolvedContext, setResolvedContext] = useState([]);
  const [, setToolStatus] = useState(null);
  const [activePlan, setActivePlan] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const abortRef = useRef(null);
  const streamTargetRef = useRef('');
  const displayedLenRef = useRef(0);
  const typewriterRafRef = useRef(null);
  const pendingPlanningResponseRef = useRef(null);
  const planningHydrationKeysRef = useRef(new Set());
  const planningLoadingMessageKeysRef = useRef(new Set());
  const prevJobsiteIdRef = useRef(jobsiteId);
  const prevInitialConversationIdRef = useRef(undefined);
  const skipNextConversationNotifyRef = useRef(false);
  const isFirstConversationNotifyRef = useRef(true);
  const scopeBarRef = useRef(null);

  const documentListFilters = useMemo(() => ({ status: 'ready', limit: 200 }), []);
  const { documents: readyDocuments, loading: scopeDocsLoading, error: scopeDocsError } =
    useBoltDocuments(documentListFilters);

  const eligibleScopeDocuments = useMemo(
    () => filterEligibleDocumentsForJobsite(jobsiteId, readyDocuments),
    [jobsiteId, readyDocuments]
  );

  const [selectedDocumentIds, setSelectedDocumentIds] = useState([]);
  const [scopePickerOpen, setScopePickerOpen] = useState(false);
  const [scopeSearch, setScopeSearch] = useState('');
  const [sourceSummaryOpen, setSourceSummaryOpen] = useState({});

  const { data: fetchedSessionStatus } = useQuery({
    queryKey: ['planning-session', planningSessionId],
    queryFn: async () => {
      const raw = await getPlanningSession(planningSessionId);
      return normalizePlanningSession(raw?.data ?? raw)?.status;
    },
    enabled: Boolean(planningSessionId) && sessionStatusProp === undefined,
    refetchInterval: (query) => {
      const s = query.state.data;
      return s && TERMINAL_PLANNING_STATUSES.has(s) ? false : 5000;
    },
  });

  const sessionStatus = sessionStatusProp ?? fetchedSessionStatus;

  useEffect(() => () => {
    if (typewriterRafRef.current != null) {
      cancelAnimationFrame(typewriterRafRef.current);
    }
  }, []);

  useEffect(() => {
    if (!jobsiteId) {
      setSelectedDocumentIds([]);
      setScopePickerOpen(false);
      setScopeSearch('');
    }
  }, [jobsiteId]);

  useEffect(() => {
    const eligibleSet = new Set(eligibleScopeDocuments.map((d) => String(d.id)));
    setSelectedDocumentIds((prev) => prev.filter((id) => eligibleSet.has(String(id))));
  }, [eligibleScopeDocuments]);

  useEffect(() => {
    if (!scopePickerOpen) return;
    const onPointerDown = (e) => {
      if (scopeBarRef.current && !scopeBarRef.current.contains(e.target)) {
        setScopePickerOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [scopePickerOpen]);

  const startNewConversation = useCallback(() => {
    setConversationId(null);
    setInput('');
    setMessages([
      {
        role: 'assistant',
        content: WELCOME_MESSAGE,
        timestamp: new Date(),
      },
    ]);
    setConversationHistory([]);
    setResolvedContext([]);
    setToolStatus(null);
    setActivePlan(null);
    setSourceSummaryOpen({});
  }, []);

  const loadConversation = useCallback(async (convId) => {
    const data = await boltAPI.loadConversation(convId);
    if (!data) return;
    const cid = data.conversationId || convId;
    skipNextConversationNotifyRef.current = true;
    setConversationId(cid);
    const msgs = Array.isArray(data.messages) ? data.messages : [];
    setMessages(
      msgs.map((m) => ({
        role: m.role,
        content: m.content,
        timestamp: m.timestamp ? new Date(m.timestamp) : new Date(),
      }))
    );
    setConversationHistory(msgs.map((m) => ({ role: m.role, content: m.content })));
    setInput('');
    setResolvedContext([]);
    setToolStatus(null);
    setActivePlan(null);
    setSourceSummaryOpen({});
  }, []);

  useEffect(() => {
    if (prevJobsiteIdRef.current !== jobsiteId && prevJobsiteIdRef.current !== undefined) {
      setConversationId(null);
      setSelectedDocumentIds([]);
      setScopePickerOpen(false);
      setScopeSearch('');
      const contextMsg = jobsiteId
        ? 'Switched context. I now have access to documents, crew, and hazard data for this jobsite.'
        : 'Switched to general mode. I now have access to all your organization\u2019s documents and data across all jobsites.';
      setMessages([
        {
          role: 'assistant',
          content: contextMsg,
          isSystem: true,
          timestamp: new Date(),
        },
      ]);
      setConversationHistory([]);
      setResolvedContext([]);
      setToolStatus(null);
      setActivePlan(null);
      prevInitialConversationIdRef.current = null;
    }
    prevJobsiteIdRef.current = jobsiteId;
  }, [jobsiteId]);

  useEffect(() => {
    if (initialConversationId) {
      if (initialConversationId !== conversationId) {
        loadConversation(initialConversationId);
      }
    } else if (prevInitialConversationIdRef.current != null && initialConversationId === null) {
      startNewConversation();
    }
    prevInitialConversationIdRef.current = initialConversationId;
  }, [initialConversationId, conversationId, loadConversation, startNewConversation]);

  useEffect(() => {
    if (isFirstConversationNotifyRef.current) {
      isFirstConversationNotifyRef.current = false;
      return;
    }
    if (skipNextConversationNotifyRef.current) {
      skipNextConversationNotifyRef.current = false;
      return;
    }
    onConversationChange?.(conversationId);
  }, [conversationId, onConversationChange]);

  // Auto-scroll on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView?.({ behavior: 'smooth' });
  }, [messages]);

  // Path-2 hydration: inject BoltPlanningCard from session question rounds (not SSE).
  // Mutually exclusive with Path-1 SSE handler — only runs when planningSessionId is set.
  useEffect(() => {
    if (!planningSessionId || !planningSessionHydrationKey) return;
    const roundQuestions = pendingQuestionRound?.questions;
    if (!roundQuestions?.length) return;
    if (planningHydrationKeysRef.current.has(planningSessionHydrationKey)) return;
    planningHydrationKeysRef.current.add(planningSessionHydrationKey);

    const mapped = mapSessionQuestionsToBoltCard(pendingQuestionRound, {
      planningSessionId,
      roundNumber: pendingQuestionRound.roundNumber ?? 1,
    });

    setActivePlan({ ...mapped, submitted: false });
    setMessages((prev) => {
      const withoutLoading = prev.filter((m) => !m.planningLoading);
      const updated = [...withoutLoading];
      const last = updated[updated.length - 1];
      if (last?.role === 'assistant' && !last.planningQuestions) {
        updated[updated.length - 1] = {
          ...last,
          planningQuestions: mapped,
          content: last.content || '',
          streaming: false,
          planningLoading: false,
        };
      } else {
        updated.push({
          role: 'assistant',
          content: '',
          planningQuestions: mapped,
          timestamp: new Date(),
          streaming: false,
        });
      }
      return updated;
    });
    // messages intentionally omitted — StrictMode double-invoke would duplicate cards
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingQuestionRound, planningSessionId, planningSessionHydrationKey]);

  // Async-delay UX while LLM prepares questions (LD-3).
  useEffect(() => {
    if (!planningSessionId || !sessionStatus) return;

    const roundQuestions = pendingQuestionRound?.questions ?? [];
    const hasQuestions = roundQuestions.length > 0;
    if (sessionStatus === 'questions_pending' && hasQuestions) return;

    const loadingKey = `${planningSessionId}:${sessionStatus}`;
    let copy = null;
    if (
      sessionStatus === 'gathering_context' ||
      sessionStatus === 'gathering-context'
    ) {
      if (!hasQuestions) copy = PLANNING_LOADING_GATHERING;
    } else if (sessionStatus === 'refining') {
      copy = PLANNING_LOADING_REFINING;
    }
    if (!copy) return;
    if (planningLoadingMessageKeysRef.current.has(loadingKey)) return;
    planningLoadingMessageKeysRef.current.add(loadingKey);

    setMessages((prev) => {
      const withoutLoading = prev.filter((m) => !m.planningLoading);
      return [
        ...withoutLoading,
        {
          role: 'assistant',
          content: copy,
          planningLoading: true,
          timestamp: new Date(),
        },
      ];
    });
  }, [planningSessionId, sessionStatus, pendingQuestionRound]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const getAuthToken = () => {
    return localStorage.getItem('vh_auth_token');
  };

  /**
   * Attempt to refresh the JWT token via the adapter.
   * Returns the new token or null on failure.
   */
  const tryRefreshToken = async () => {
    try {
      const { default: jwtAdapter } = await import('../../../auth/adapters/jwtAdapter.jsx');
      if (typeof jwtAdapter?.refreshAuthToken === 'function') {
        return await jwtAdapter.refreshAuthToken();
      }
    } catch (e) {
      // Refresh not available (e.g., Firebase auth mode) — ignore
    }
    return null;
  };

  const handleSend = useCallback(async (overrideText) => {
    const raw = typeof overrideText === 'string' ? overrideText : input;
    const text = raw.trim();
    if (!text || streaming) return;

    // Add user message
    const userMsg = { role: 'user', content: text, timestamp: new Date() };
    setMessages((prev) => [...prev, userMsg]);
    if (typeof overrideText !== 'string') {
      setInput('');
    }
    setStreaming(true);

    if (typewriterRafRef.current != null) {
      cancelAnimationFrame(typewriterRafRef.current);
      typewriterRafRef.current = null;
    }
    streamTargetRef.current = '';
    displayedLenRef.current = 0;

    const stopTypewriter = () => {
      if (typewriterRafRef.current != null) {
        cancelAnimationFrame(typewriterRafRef.current);
        typewriterRafRef.current = null;
      }
    };

    const applyDisplayed = (next) => {
      displayedLenRef.current = next.length;
      setMessages((prev) => {
        const updated = [...prev];
        const last = updated[updated.length - 1];
        if (last?.role === 'assistant' && last.content !== next) {
          updated[updated.length - 1] = { ...last, content: next };
          return updated;
        }
        return prev;
      });
    };

    const tickTypewriter = () => {
      const target = streamTargetRef.current;
      const shown = displayedLenRef.current;
      if (shown >= target.length) {
        typewriterRafRef.current = null;
        return;
      }
      const nextLen = Math.min(target.length, shown + typewriterStep(target.length - shown));
      applyDisplayed(target.slice(0, nextLen));
      typewriterRafRef.current = requestAnimationFrame(tickTypewriter);
    };

    const enqueueStreamText = (chunk) => {
      if (!chunk) return;
      streamTargetRef.current += chunk;
      if (
        prefersReducedMotion()
        || (typeof process !== 'undefined' && process.env?.VITEST)
      ) {
        applyDisplayed(streamTargetRef.current);
        return;
      }
      if (typewriterRafRef.current == null) {
        typewriterRafRef.current = requestAnimationFrame(tickTypewriter);
      }
    };

    // Build history for context
    const history = [...conversationHistory, { role: 'user', content: text }];

    // Placeholder for assistant streaming response
    const assistantMsg = {
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      sources: [],
      intent: null,
      streaming: true,
      reasoning: [],
      reasoningActive: true,
      reasoningStartedAt: Date.now(),
      reasoningMs: 0,
    };
    setMessages((prev) => [...prev, assistantMsg]);

    try {
      const token = getAuthToken();
      const controller = new AbortController();
      abortRef.current = controller;

      const resolvedEntities = resolvedContext.reduce((acc, ctx) => {
        acc[`${ctx.type}Id`] = ctx.id;
        return acc;
      }, {});

      const streamBody = {
        message: text,
        conversationId: conversationId || undefined,
        contextJobsiteId: jobsiteId || undefined,
        conversationHistory: history.slice(-10),
        ...resolvedEntities,
        ...(pendingPlanningResponseRef.current
          ? { planningResponse: pendingPlanningResponseRef.current }
          : {}),
        ...(selectedDocumentIds.length > 0 ? { documentIds: selectedDocumentIds } : {}),
        ...(planningSessionId ? { planningSessionId } : {}),
      };

      let response = await fetch(`${API_BASE_URL}/api/bolt/chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(streamBody),
        signal: controller.signal,
      });

      // On 401, attempt one token refresh and retry
      if (response.status === 401) {
        const newToken = await tryRefreshToken();
        if (newToken) {
          const retryResponse = await fetch(`${API_BASE_URL}/api/bolt/chat/stream`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${newToken}`,
            },
            body: JSON.stringify(streamBody),
            signal: controller.signal,
          });
          if (!retryResponse.ok) {
            const errData = await retryResponse.json().catch(() => ({}));
            throw new Error(errData.error || `Request failed after token refresh: ${retryResponse.status}`);
          }
          // Replace response with the retry response for stream reading below
          response = retryResponse;
        } else {
          throw new Error('Session expired. Please log in again.');
        }
      } else if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || `Request failed: ${response.status}`);
      }

      // Read SSE stream
      // Server sends named SSE events: "event: <type>\ndata: <json>\n\n"
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let fullText = '';
      let sources = [];
      let citedSources = null; // null = no cited event yet; [] = searched but nothing grounded
      let intent = null;
      let disambiguation = null;
      let contextData = null;

      const dispatchSSEPayload = (eventType, payload) => {
        if (payload === '[DONE]') return;

        try {
          let data;
          try {
            data = JSON.parse(payload);
          } catch {
            // data might be a raw string (e.g. text chunks)
            data = payload;
          }

          const type = eventType || data?.type;

          switch (type) {
              case 'tool_status': {
                const tools = data?.tools || data?.data?.tools || [];
                if (tools.length > 0) {
                  setToolStatus(tools);
                  setMessages((prev) => {
                    let next = prev;
                    tools.forEach((name) => {
                      next = appendAssistantReasoning(next, {
                        type: 'tool',
                        name,
                        text: `Looking up ${describeBoltTool(name)}`,
                      });
                    });
                    return next;
                  });
                }
                break;
              }

              case 'thinking': {
                const chunk = typeof data === 'string' ? data : (data?.text || data?.content || '');
                if (!chunk) break;
                setMessages((prev) => appendAssistantReasoning(
                  prev,
                  { type: 'thought', text: chunk },
                  { mergeThought: true }
                ));
                break;
              }

              case 'text': {
                const chunk = typeof data === 'string' ? data : (data?.text || data?.content || '');
                if (chunk) setToolStatus(null);
                fullText += chunk;
                if (chunk) {
                  setMessages((prev) => sealAssistantReasoning(prev));
                }
                enqueueStreamText(chunk);
                break;
              }

              case 'rag_sources':
                sources = Array.isArray(data) ? data : (data?.sources || []);
                setMessages((prev) => {
                  const updated = [...prev];
                  const last = updated[updated.length - 1];
                  if (last?.role === 'assistant') {
                    updated[updated.length - 1] = { ...last, sources };
                  }
                  return updated;
                });
                break;

              case 'rag_sources_cited':
                // Documents the answer actually grounded on (ISS-374 pass-1b).
                // [] = documents were searched but none grounded the answer
                // (answered from general knowledge).
                citedSources = Array.isArray(data) ? data : (data?.sources || []);
                setMessages((prev) => {
                  const updated = [...prev];
                  const last = updated[updated.length - 1];
                  if (last?.role === 'assistant') {
                    updated[updated.length - 1] = { ...last, citedSources };
                  }
                  return updated;
                });
                break;

              case 'intent':
                intent = data?.intent || data;
                setMessages((prev) => {
                  const updated = [...prev];
                  const last = updated[updated.length - 1];
                  if (last?.role === 'assistant') {
                    updated[updated.length - 1] = { ...last, intent };
                  }
                  return updated;
                });
                break;

              case 'disambiguation':
                disambiguation = data?.ambiguities || data;
                setMessages((prev) => {
                  const updated = [...prev];
                  const last = updated[updated.length - 1];
                  if (last?.role === 'assistant') {
                    updated[updated.length - 1] = { ...last, disambiguation };
                  }
                  return updated;
                });
                break;

              case 'context_data':
                contextData = data;
                setMessages((prev) => {
                  const updated = [...prev];
                  const last = updated[updated.length - 1];
                  if (last?.role === 'assistant') {
                    updated[updated.length - 1] = { ...last, contextData };
                  }
                  return updated;
                });
                break;

              case 'planning_questions': {
                if (planningSessionId) {
                  // Path-2 mode: hydration is owned by the page-level useEffect; ignore SSE-side
                  // planning_questions injection to prevent duplicate-card race per reviewer-14
                  // verdict MEDIUM-1 (2026-05-28).
                  break;
                }
                const planData = data;
                setActivePlan({
                  planId: planData.planId,
                  context: planData.context,
                  questions: planData.questions || [],
                  submitted: false,
                });
                setMessages((prev) => {
                  const updated = [...prev];
                  const last = updated[updated.length - 1];
                  if (last?.role === 'assistant') {
                    updated[updated.length - 1] = {
                      ...last,
                      planningQuestions: planData,
                      streaming: false,
                      reasoningActive: false,
                    };
                  }
                  return updated;
                });
                setToolStatus(null);
                break;
              }

              case 'entity_resolved':
                if (data?.type && data?.id && data?.name) {
                  setResolvedContext((prev) => {
                    const exists = prev.some(
                      (c) => c.type === data.type && c.id === data.id
                    );
                    return exists ? prev : [...prev, data];
                  });
                }
                break;

              case 'error':
                throw new Error(data?.error || data?.message || 'Stream error');

              case 'done':
                setToolStatus(null);
                if (data?.fullText) {
                  fullText = data.fullText;
                }
                if (data?.conversationId) {
                  setConversationId(data.conversationId);
                }
                stopTypewriter();
                streamTargetRef.current = fullText;
                displayedLenRef.current = fullText.length;
                setMessages((prev) => {
                  const updated = [...prev];
                  const last = updated[updated.length - 1];
                  if (last?.role === 'assistant') {
                    updated[updated.length - 1] = { ...last, content: fullText };
                  }
                  return updated;
                });
                break;

              default:
                break;
            }
        } catch (parseErr) {
          if (parseErr instanceof Error && parseErr.message !== 'Stream error') {
            throw parseErr; // Re-throw actual errors from error events
          }
        }
      };

      const processCompleteSSEEvents = () => {
        let separatorIndex;
        while ((separatorIndex = buffer.indexOf('\n\n')) !== -1) {
          const rawEvent = buffer.slice(0, separatorIndex);
          buffer = buffer.slice(separatorIndex + 2);
          if (!rawEvent.trim()) continue;
          const parsed = parseSSEBlock(rawEvent);
          if (!parsed) continue;
          dispatchSSEPayload(parsed.eventType, parsed.payload);
        }
      };

      while (true) {
        const { done, value } = await reader.read();
        if (value) {
          buffer += decoder.decode(value, { stream: true });
          processCompleteSSEEvents();
        }
        if (done) {
          // Flush trailing event that may lack a final blank-line delimiter
          if (buffer.trim()) {
            const parsed = parseSSEBlock(buffer);
            buffer = '';
            if (parsed) dispatchSSEPayload(parsed.eventType, parsed.payload);
          }
          break;
        }
      }

      stopTypewriter();
      streamTargetRef.current = fullText;
      displayedLenRef.current = fullText.length;
      setMessages((prev) => {
        const updated = [...prev];
        const last = updated[updated.length - 1];
        if (last?.role === 'assistant') {
          updated[updated.length - 1] = {
            ...last,
            content: fullText,
            sources,
            ...(citedSources !== null && { citedSources }),
            intent,
            disambiguation,
            contextData,
            streaming: false,
            reasoningActive: false,
          };
        }
        return updated;
      });

      // Update conversation history
      setConversationHistory([
        ...history,
        { role: 'assistant', content: fullText },
      ]);
    } catch (err) {
      stopTypewriter();
      if (err.name === 'AbortError') return;

      setMessages((prev) => {
        const updated = [...prev];
        const last = updated[updated.length - 1];
        if (last?.role === 'assistant' && last.streaming) {
          updated[updated.length - 1] = {
            ...last,
            content: `⚠️ ${err.message || 'Something went wrong. Please try again.'}`,
            streaming: false,
            reasoningActive: false,
            error: true,
          };
        }
        return updated;
      });
    } finally {
      stopTypewriter();
      setStreaming(false);
      setToolStatus(null);
      abortRef.current = null;
      pendingPlanningResponseRef.current = null;
    }
  }, [
    input,
    streaming,
    conversationHistory,
    resolvedContext,
    conversationId,
    jobsiteId,
    selectedDocumentIds,
  ]);

  const filteredScopeDocuments = useMemo(() => {
    const q = scopeSearch.trim().toLowerCase();
    if (!q) return eligibleScopeDocuments;
    return eligibleScopeDocuments.filter(
      (d) =>
        (d.name || '').toLowerCase().includes(q) ||
        (d.fileName || '').toLowerCase().includes(q)
    );
  }, [eligibleScopeDocuments, scopeSearch]);

  const toggleDocumentScope = useCallback((docId) => {
    const sid = String(docId);
    setSelectedDocumentIds((prev) =>
      prev.includes(sid) ? prev.filter((x) => x !== sid) : [...prev, sid]
    );
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleVoiceTranscript = (transcript) => {
    setInput((prev) => (prev ? `${prev} ${transcript}` : transcript));
    inputRef.current?.focus();
  };

  const handleDisambiguationResolve = useCallback(
    (type, candidate) => {
      setResolvedContext((prev) => {
        const filtered = prev.filter((c) => c.type !== type);
        return [...filtered, { type, id: candidate.id, name: candidate.name }];
      });
      const lastUserMsg = [...messages].reverse().find((m) => m.role === 'user');
      if (lastUserMsg) {
        setInput(lastUserMsg.content);
        setTimeout(() => inputRef.current?.focus(), 0);
      }
    },
    [messages]
  );

  const handlePlanningSubmit = useCallback(
    async (planIdOrPayload, answersArg) => {
      let planId;
      let answers;
      let sessionIdFromCard;
      if (typeof planIdOrPayload === 'object' && planIdOrPayload?.planId) {
        planId = planIdOrPayload.planId;
        answers = planIdOrPayload.answers;
        sessionIdFromCard = planIdOrPayload.planningSessionId;
      } else {
        planId = planIdOrPayload;
        answers = answersArg;
      }
      const effectiveSessionId = planningSessionId || sessionIdFromCard;
      const mappedAnswers = (answers || []).map((a) => ({
        questionId: a.id,
        answerText: a.value ?? '',
      }));

      if (effectiveSessionId && onPlanningSessionAnswersSubmit && mappedAnswers.length > 0) {
        try {
          await onPlanningSessionAnswersSubmit({ answers: mappedAnswers });
        } catch (err) {
          setMessages((prev) => [
            ...prev,
            {
              role: 'assistant',
              content: `Could not save planning answers: ${err?.message || 'Please try again.'}`,
              timestamp: new Date(),
            },
          ]);
          return;
        }
      }

      setActivePlan((prev) => (prev ? { ...prev, submitted: true } : null));
      const summary = answers.map((a) => `${a.id}: ${a.value}`).join(', ');
      if (!effectiveSessionId) {
        pendingPlanningResponseRef.current = {
          planId,
          answers,
        };
        handleSend(`Here are my answers: ${summary}`);
      }
      setTimeout(() => setActivePlan(null), 100);
    },
    [handleSend, onPlanningSessionAnswersSubmit, planningSessionId]
  );

  const handlePlanningSkip = useCallback(() => {
    setActivePlan(null);
    setMessages((prev) => {
      const updated = [...prev];
      const last = updated[updated.length - 1];
      if (last?.role === 'assistant' && last.planningQuestions) {
        const { planningQuestions, ...rest } = last;
        updated[updated.length - 1] = rest;
      }
      return updated;
    });
  }, []);

  const handleDismissContext = useCallback((type, id) => {
    setResolvedContext((prev) => prev.filter((c) => !(c.type === type && c.id === id)));
  }, []);

  const handleIntentConfirm = async (intent) => {
    try {
      const token = getAuthToken();
      const res = await fetch(`${API_BASE_URL}/api/bolt/intent/confirm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: intent.action,
          parameters: intent.parameters,
        }),
      });
      const data = await res.json();

      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: data.success
            ? `✅ Done — ${intent.action} completed successfully.`
            : `❌ Failed — ${data.error || 'Could not complete action.'}`,
          timestamp: new Date(),
        },
      ]);
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: '❌ Failed to execute action. Please try again.',
          timestamp: new Date(),
          error: true,
        },
      ]);
    }
  };

  const suggestedQueries = [
    'What PPE do I need for concrete work?',
    'What are the fall protection requirements?',
    'Who is the competent person for scaffolding?',
    'What are the heat illness rest break intervals?',
  ];

  return (
    <div className="bolt-chat">
      {planningSessionId && sessionStatus ? (
        <PlanningSessionStatusBadge
          sessionId={planningSessionId}
          status={sessionStatus}
          className="bolt-chat-planning-status"
        />
      ) : null}
      <div className="bolt-chat-scope-wrap" ref={scopeBarRef}>
        {!jobsiteId ? (
          <p className="bolt-chat-scope-hint">
            Select a jobsite to scope Bolt to specific documents.
          </p>
        ) : (
          <>
            <div className="bolt-chat-scope-bar">
              <button
                type="button"
                className="bolt-chat-scope-trigger"
                data-testid="bolt-chat-scope-trigger"
                aria-expanded={scopePickerOpen}
                aria-controls="bolt-chat-scope-panel"
                id="bolt-chat-scope-trigger-btn"
                onClick={() => setScopePickerOpen((o) => !o)}
              >
                <FileText size={14} aria-hidden />
                <span>Document scope</span>
                {selectedDocumentIds.length > 0 && (
                  <span className="bolt-chat-scope-trigger__count">{selectedDocumentIds.length}</span>
                )}
                <ChevronDown
                  size={14}
                  className={
                    scopePickerOpen ? 'bolt-chat-scope-trigger__chev bolt-chat-scope-trigger__chev--open' : 'bolt-chat-scope-trigger__chev'
                  }
                  aria-hidden
                />
              </button>
              {selectedDocumentIds.length > 0 && (
                <button
                  type="button"
                  className="bolt-chat-scope-clear"
                  data-testid="bolt-chat-scope-clear"
                  onClick={() => setSelectedDocumentIds([])}
                >
                  Clear selection
                </button>
              )}
            </div>
            {selectedDocumentIds.length > 0 && (
              <div className="bolt-chat-scope-chips">
                {selectedDocumentIds.map((id) => {
                  const doc = eligibleScopeDocuments.find((d) => String(d.id) === id);
                  const label = doc?.name || doc?.fileName || id;
                  return (
                    <span key={id} className="bolt-chat-scope-chip">
                      <span className="bolt-chat-scope-chip__label">{label}</span>
                      <button
                        type="button"
                        className="bolt-chat-scope-chip__remove"
                        aria-label={`Remove ${label} from scope`}
                        onClick={() =>
                          setSelectedDocumentIds((prev) => prev.filter((x) => x !== id))
                        }
                      >
                        <X size={12} />
                      </button>
                    </span>
                  );
                })}
              </div>
            )}
            {scopePickerOpen && (
              <div
                className="bolt-chat-scope-panel"
                id="bolt-chat-scope-panel"
                role="region"
                aria-labelledby="bolt-chat-scope-trigger-btn"
              >
                <label className="bolt-chat-scope-search-label" htmlFor="bolt-chat-scope-search">
                  Search documents
                </label>
                <input
                  id="bolt-chat-scope-search"
                  type="search"
                  className="bolt-chat-scope-search"
                  placeholder="Filter by name…"
                  value={scopeSearch}
                  onChange={(e) => setScopeSearch(e.target.value)}
                />
                {scopeDocsLoading && (
                  <p className="bolt-chat-scope-panel__meta" data-testid="bolt-chat-scope-loading">
                    Loading documents…
                  </p>
                )}
                {scopeDocsError && !scopeDocsLoading && (
                  <p className="bolt-chat-scope-panel__error">Could not load documents.</p>
                )}
                {!scopeDocsLoading && !scopeDocsError && filteredScopeDocuments.length === 0 && (
                  <p className="bolt-chat-scope-panel__empty">No matching ready documents.</p>
                )}
                {!scopeDocsLoading && !scopeDocsError && filteredScopeDocuments.length > 0 && (
                  <ul className="bolt-chat-scope-list">
                    {filteredScopeDocuments.map((doc) => {
                      const sid = String(doc.id);
                      const checked = selectedDocumentIds.includes(sid);
                      return (
                        <li key={sid}>
                          <label className="bolt-chat-scope-row">
                            <input
                              type="checkbox"
                              data-testid={`bolt-doc-scope-${sid}`}
                              checked={checked}
                              onChange={() => toggleDocumentScope(doc.id)}
                            />
                            <span className="bolt-chat-scope-row__title">
                              {doc.name || doc.fileName || sid}
                            </span>
                            {doc.scope === 'company' && (
                              <span className="bolt-chat-scope-row__badge">Company</span>
                            )}
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Messages area */}
      <div className="bolt-chat-messages">
        {messages.map((msg, i) => {
          if (isEmptyStreamingAssistant(msg)) return null;
          return (
          <div
            key={i}
            className={`bolt-chat-msg bolt-chat-msg--${msg.role}${msg.streaming ? ' bolt-chat-msg--live' : ''}`}
          >
            <div className="bolt-chat-msg__body">
              {(msg.reasoningActive || msg.reasoning?.length > 0) && (
                <BoltReasoning
                  parts={msg.reasoning || []}
                  active={Boolean(msg.reasoningActive && msg.streaming)}
                  durationMs={msg.reasoningMs || 0}
                />
              )}
              {msg.content ? (
                <div
                  className={`bolt-chat-msg__content ${msg.error ? 'bolt-chat-msg__content--error' : ''}`}
                >
                  {msg.error ? msg.content : <BoltMarkdown>{msg.content}</BoltMarkdown>}
                  {msg.streaming && <span className="bolt-chat-cursor" aria-hidden="true" />}
                </div>
              ) : null}

              {/* Grounded-source badge (ISS-374 pass-1b). The collapsible panel
                  is driven by the CITED set (what the answer grounded on); the
                  "searched" count reflects retrieved documents. When documents
                  were searched but none cited, show the honest empty state. When
                  no cited event arrived (legacy / non-RAG), fall back to the
                  searched-only badge. */}
              {(() => {
                const searchedCount = groupSourcesForSummary(msg.sources).documentCount;

                // Searched but nothing grounded → honest "general knowledge" state.
                if (Array.isArray(msg.citedSources) && msg.citedSources.length === 0) {
                  if (!(msg.sources?.length > 0)) return null;
                  return (
                    <div className="bolt-chat-source-summary bolt-chat-source-summary--ungrounded">
                      <span className="bolt-chat-source-summary__note">
                        Answered from general knowledge — no project documents grounded this answer
                        {searchedCount > 0
                          ? ` (${searchedCount} ${searchedCount === 1 ? 'document' : 'documents'} searched).`
                          : '.'}
                      </span>
                    </div>
                  );
                }

                // Grounded → panel driven by the cited set, labelled cited · searched.
                if (Array.isArray(msg.citedSources) && msg.citedSources.length > 0) {
                  const grouped = groupSourcesForSummary(msg.citedSources);
                  const expanded = !!sourceSummaryOpen[i];
                  return (
                    <div className="bolt-chat-source-summary">
                      <button
                        type="button"
                        className="bolt-chat-source-summary__toggle"
                        aria-expanded={expanded}
                        onClick={() =>
                          setSourceSummaryOpen((prev) => ({ ...prev, [i]: !prev[i] }))
                        }
                      >
                        <FileText size={14} aria-hidden />
                        <span>
                          Grounded: {grouped.documentCount} cited
                          {searchedCount > 0 ? ` · ${searchedCount} searched` : ''}
                        </span>
                        <ChevronDown
                          size={14}
                          className={
                            expanded
                              ? 'bolt-chat-source-summary__chev bolt-chat-source-summary__chev--open'
                              : 'bolt-chat-source-summary__chev'
                          }
                          aria-hidden
                        />
                      </button>
                      {expanded && (
                        <ul className="bolt-chat-source-summary__list">
                          {grouped.groups.map((g) => (
                            <li key={g.key} className="bolt-chat-source-summary__row">
                              {g.title}
                              {g.sheets.length === 1
                                ? ` — sheet ${g.sheets[0]}`
                                : g.sheets.length > 1
                                  ? ` — sheets ${g.sheets.join(', ')}`
                                  : ''}{' '}
                              ({g.count} {g.count === 1 ? 'passage' : 'passages'})
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                }

                // Cited event not yet received while streaming → pending (ISS-381).
                if (msg.streaming && msg.citedSources == null && msg.sources?.length > 0) {
                  return (
                    <div className="bolt-chat-source-summary bolt-chat-source-summary--pending">
                      <span className="bolt-chat-source-summary__note">
                        Searching project documents…
                      </span>
                    </div>
                  );
                }

                // No cited event (legacy / non-RAG path) → searched-only fallback.
                if (msg.sources?.length > 0) {
                  const grouped = groupSourcesForSummary(msg.sources);
                  const expanded = !!sourceSummaryOpen[i];
                  return (
                    <div className="bolt-chat-source-summary">
                      <button
                        type="button"
                        className="bolt-chat-source-summary__toggle"
                        aria-expanded={expanded}
                        onClick={() =>
                          setSourceSummaryOpen((prev) => ({ ...prev, [i]: !prev[i] }))
                        }
                      >
                        <FileText size={14} aria-hidden />
                        <span>
                          {grouped.documentCount} related{' '}
                          {grouped.documentCount === 1 ? 'document' : 'documents'} ({grouped.totalSources}{' '}
                          {grouped.totalSources === 1 ? 'passage' : 'passages'})
                        </span>
                        <ChevronDown
                          size={14}
                          className={
                            expanded
                              ? 'bolt-chat-source-summary__chev bolt-chat-source-summary__chev--open'
                              : 'bolt-chat-source-summary__chev'
                          }
                          aria-hidden
                        />
                      </button>
                      {expanded && (
                        <ul className="bolt-chat-source-summary__list">
                          {grouped.groups.map((g) => (
                            <li key={g.key} className="bolt-chat-source-summary__row">
                              {g.title}
                              {g.sheets.length === 1
                                ? ` — sheet ${g.sheets[0]}`
                                : g.sheets.length > 1
                                  ? ` — sheets ${g.sheets.join(', ')}`
                                  : ''}{' '}
                              ({g.count} {g.count === 1 ? 'passage' : 'passages'})
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                }

                return null;
              })()}

              {/* Intent confirmation card (legacy intent system) */}
              {msg.intent && msg.intent.strategy === 'confirm' && (
                <div className="bolt-chat-intent">
                  <Zap size={14} />
                  <span>
                    Action: <strong>{msg.intent.action}</strong> (confidence:{' '}
                    {Math.round(msg.intent.confidence * 100)}%)
                  </span>
                  <div className="bolt-chat-intent__actions">
                    <button
                      className="bolt-chat-intent__btn bolt-chat-intent__btn--confirm"
                      onClick={() => handleIntentConfirm(msg.intent)}
                    >
                      <CheckCircle size={14} /> Confirm
                    </button>
                    <button className="bolt-chat-intent__btn bolt-chat-intent__btn--cancel">
                      <XCircle size={14} /> Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Mutation confirmation quick-action (tool-use flow) */}
              {!msg.streaming && msg.role === 'assistant' && i === messages.length - 1 &&
                /\b(confirm|go ahead|proceed|should i|want me to)\b/i.test(msg.content) &&
                /\b(create|file|assign|schedule|send|update|generate|escalate)\b/i.test(msg.content) && (
                <div className="bolt-chat-confirm">
                  <button
                    className="bolt-chat-confirm__btn bolt-chat-confirm__btn--yes"
                    onClick={() => handleSend('Yes, go ahead')}
                  >
                    <CheckCircle size={14} /> Confirm
                  </button>
                  <button
                    className="bolt-chat-confirm__btn bolt-chat-confirm__btn--no"
                    onClick={() => handleSend('No, cancel that')}
                  >
                    <XCircle size={14} /> Cancel
                  </button>
                </div>
              )}

              {/* Disambiguation card */}
              {msg.disambiguation?.length > 0 && (
                <BoltDisambiguationCard
                  ambiguities={msg.disambiguation}
                  onResolve={handleDisambiguationResolve}
                />
              )}

              {/* Planning questions card */}
              {msg.planningQuestions && (
                planningSessionId ? (
                  <div
                    className="bolt-planning-card-hydrated"
                    data-testid="bolt-planning-card-hydrated"
                  >
                    <BoltPlanningCard
                      planId={msg.planningQuestions.planId}
                      context={msg.planningQuestions.context}
                      questions={msg.planningQuestions.questions}
                      onSubmit={handlePlanningSubmit}
                      onSkip={handlePlanningSkip}
                      disabled={activePlan?.submitted || activePlan === null}
                      planningSessionId={planningSessionId}
                    />
                  </div>
                ) : (
                  <BoltPlanningCard
                    planId={msg.planningQuestions.planId}
                    context={msg.planningQuestions.context}
                    questions={msg.planningQuestions.questions}
                    onSubmit={handlePlanningSubmit}
                    onSkip={handlePlanningSkip}
                    disabled={activePlan?.submitted || activePlan === null}
                    planningSessionId={planningSessionId}
                  />
                )
              )}

              {/* Structured data card */}
              {msg.contextData && (
                <BoltDataCard
                  type={msg.contextData.type}
                  label={msg.contextData.label}
                  items={msg.contextData.items}
                />
              )}
            </div>
          </div>
          );
        })}

        {streaming && isEmptyStreamingAssistant(messages[messages.length - 1]) && (
          <div className="bolt-chat-typing">
            <BoltShiningText text="Bolt is thinking…" />
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Suggested queries (only when no user messages yet) */}
      {messages.length <= 1 && !planningSessionId && (
        <div className="bolt-chat-suggestions">
          <span className="bolt-chat-suggestions__label">
            <Sparkles size={13} />
            Try asking
          </span>
          <div className="bolt-chat-suggestions__list">
            {suggestedQueries.map((q, i) => (
              <button
                key={i}
                className="bolt-chat-suggestion"
                onClick={() => {
                  setInput(q);
                  inputRef.current?.focus();
                }}
              >
                <ArrowUpRight size={14} className="bolt-chat-suggestion__arrow" />
                <span>{q}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Context indicator chips */}
      {resolvedContext.length > 0 && (
        <div className="bolt-chat-context-chips">
          {resolvedContext.map((ctx) => (
            <span
              key={`${ctx.type}-${ctx.id}`}
              className="bolt-chat-context-chip"
            >
              <span className="bolt-chat-context-chip__icon" aria-hidden="true">
                {ctx.type === 'jobsite' ? '📍' : '👤'}
              </span>
              <span className="bolt-chat-context-chip__label">{ctx.name}</span>
              <button
                className="bolt-chat-context-chip__dismiss"
                onClick={() => handleDismissContext(ctx.type, ctx.id)}
                type="button"
                aria-label={`Remove ${ctx.name} context`}
              >
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
      )}

      <BoltChatComposer
        inputRef={inputRef}
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={handleKeyDown}
        onSend={handleSend}
        disabled={streaming || (activePlan && !activePlan.submitted)}
        sendDisabled={!input.trim() || streaming || (activePlan && !activePlan.submitted)}
        nativePlaceholder={
          activePlan && !activePlan.submitted
            ? 'Answer the planning questions above…'
            : 'Ask Bolt anything…'
        }
        voice={<BoltVoiceInput onTranscript={handleVoiceTranscript} />}
      />
    </div>
  );
}
